import { Events } from "tir-pm-toolkit";
import ApiService from "@/api";

import {
  ResponseModel,
  CommentChatMessageModel,
  CreateCommentModel,
  CreateCommentResultModel,
  OnUpdateCommentModel,
  UpdateCommentModel,
  CreateCommentFileFolderModel,
  RemoveFileFromCommentModel,
} from "../models";
import {
  mapCreateCommentResponse,
  mapCreateCommentTaskRequest,
  mapCreateFolderWithoutEntityRequest,
  mapDeleteCommentTaskRequest,
  mapGetCommentListItem,
  mapGetCommentsResponse,
  mapGetCommentTaskRequest,
  mapOnUpdateCommentResponse,
  mapRemoveFileComment,
  mapUpdateCommentTaskRequest,
} from "./AdapterServiceMappers";
import WsResponseModelT from "@/api/Main/dataSource/api/schema-builder/server/models/WsResponseModelT";
import OnRemoveCommentResponse from "@/api/Main/dataSource/api/projects/task-comments/i-service/models/response/OnRemoveCommentResponse";
import OnUpdateCommentResponse from "@/api/Main/dataSource/api/projects/task-comments/i-service/models/response/OnUpdateCommentResponse";
import { PaginationRequestModel } from "@/models";
import GetCommentListItem from "@/api/Main/dataSource/api/projects/task-comments/i-service/models/response/GetCommentListItem";
import OnCompleteAddFileResponse from "@/api/File/dataSource/api/project-manager/files/i-services/models/response/OnCompleteAddFileResponse";

export class AdapterService {
  private readonly api = new ApiService();

  /**Событие создания комментария */
  OnCreateCommentEvent = new Events<CommentChatMessageModel>();
  /**Событие удаления комментария */
  OnRemoveCommentEvent = new Events<number>();
  /**Событие обновления комментария */
  OnUpdateCommentEvent = new Events<OnUpdateCommentModel>();
  /**Событие завершения загрузки файла */
  OnCompleteAddFileEvent = new Events<number>();

  constructor() {
    this.subscribeOnCreateCommentEvent();
    this.subscribeOnRemoveCommentEvent();
    this.subscribeOnUpdateCommentEvent();
    this.subscribeOnCompleteAddFileEvent();
  }

  /**
   * * Подписки на изменение комментариев
   */
  private subscribeOnCreateCommentEvent() {
    this.api.Main.TaskCommentService.OnCreateCommentEvent.on(
      (resp: WsResponseModelT<GetCommentListItem>) => {
        if (resp.IsSuccess) {
          this.OnCreateCommentEvent.trigger(mapGetCommentListItem(resp.Value));
        }
      },
    );
  }

  private subscribeOnRemoveCommentEvent() {
    this.api.Main.TaskCommentService.OnRemoveCommentEvent.on(
      (resp: WsResponseModelT<OnRemoveCommentResponse>) => {
        if (resp.IsSuccess) {
          this.OnRemoveCommentEvent.trigger(resp.Value.CommentId);
        }
      },
    );
  }

  subscribeOnUpdateCommentEvent() {
    return this.api.Main.TaskCommentService.OnUpdateCommentEvent.on(
      (resp: WsResponseModelT<OnUpdateCommentResponse>) => {
        if (resp.IsSuccess) {
          this.OnUpdateCommentEvent.trigger(mapOnUpdateCommentResponse(resp.Value));
        }
      },
    );
  }

  private subscribeOnCompleteAddFileEvent() {
    return this.api.File.FileService.OnCompleteAddFileEvent.on(
      (resp: WsResponseModelT<OnCompleteAddFileResponse>) => {
        if (resp.IsSuccess) {
          this.OnCompleteAddFileEvent.trigger(resp.Value.FileId);
        }
      },
    );
  }

  /**
   * * Получить список комментариев
   * @param taskId ID задачи
   * @param page Страница
   * @returns
   */
  getComments(taskId: number, page: PaginationRequestModel) {
    return new Promise<ResponseModel<Array<CommentChatMessageModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<CommentChatMessageModel>>();
      await this.api.Main.TaskCommentService.getCommentListAsync(
        mapGetCommentTaskRequest(taskId, page),
      )
        .then((resp) => {
          result.Value = mapGetCommentsResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR getComments", e);
          return resolve(result);
        });
    });
  }

  /**
   * * Создать комментарий
   * @param request Запрос
   * @returns
   */
  createCommentTask(request: CreateCommentModel) {
    return new Promise<ResponseModel<CreateCommentResultModel>>(async (resolve) => {
      const result = new ResponseModel<CreateCommentResultModel>();

      await this.api.Main.TaskCommentService.createCommentAsync(
        mapCreateCommentTaskRequest(request),
      )
        .then((resp) => {
          result.Value = mapCreateCommentResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR createCommentTask", e);
          return resolve(result);
        });
    });
  }

  /**
   * * Обновить коммнарий
   * @param commentId ID проекта
   * @returns
   */
  updateCommentTask(request: UpdateCommentModel) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      await this.api.Main.TaskCommentService.updateCommentAsync(
        mapUpdateCommentTaskRequest(request),
      )
        .then((resp) => {
          result.IsSuccess = resp.IsSuccess;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR updateCommentTask", e);
          return resolve(result);
        });
    });
  }

  /**
   * * Удалить коммнарий
   * @param commentId ID проекта
   * @returns
   */
  deleteCommentTask(commentId: number) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      await this.api.Main.TaskCommentService.removeCommentAsync(
        mapDeleteCommentTaskRequest(commentId),
      )
        .then((resp) => {
          result.IsSuccess = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR deleteCommentTask", e);
          return resolve(result);
        });
    });
  }

  /**
   * * Создание папки для загрузки файлов
   * @param data
   * @returns
   */
  createFolderWithoutEntity(data: CreateCommentFileFolderModel) {
    return new Promise<ResponseModel<string>>(async (resolve) => {
      const result = new ResponseModel<string>();

      await this.api.File.FolderService.createFolderWithoutEntityAsync(
        mapCreateFolderWithoutEntityRequest(data),
      )
        .then((resp) => {
          result.Value = resp.FolderId;
          return resolve(result);
        })
        .catch((e) => {
          console.error("ERROR createFolderWithoutEntity");
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          return resolve(result);
        });
    });
  }

  /**
   * Удалить файл из комментария
   * @param request Запрос
   * @returns
   */
  removeFileFromComment(request: RemoveFileFromCommentModel) {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      await this.api.Main.TaskCommentService.removeFilesAsync(mapRemoveFileComment(request))
        .then((resp) => {
          result.IsSuccess = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          console.error("ERROR removeFileFromComment");
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          return resolve(result);
        });
    });
  }
}
